/**
 * Give the page its scrollbar back after a modal is destroyed mid-flight.
 *
 * ## The bug
 *
 * `el-dialog` locks body scroll while it is open — `el-popup-parent--hidden` on
 * `<body>` plus an inline `overflow` — and releases it when it closes. Twenty-one
 * dialogs in this app are written as:
 *
 *     <el-dialog v-if="store.flag" v-model="store.flag" …>
 *
 * The `v-if` destroys the component in the same tick the `v-model` would have
 * closed it, and a component that has been torn down cannot run its own
 * release. Navigate away with one open — click a lead card, then click through
 * to the lead page — and the lock stays on `<body>`.
 *
 * Nothing in an SPA clears that. The page looks completely normal, the mouse
 * wheel does nothing, and refreshing "fixes" it, because a reload is the only
 * thing that drops the inline style. That is a confusing bug to report and an
 * even more confusing one to hunt, which is why this is a net rather than
 * twenty-one edits: the `v-if`/`v-model` pairing is a pattern the codebase
 * plainly likes, and it will be written again.
 *
 * ## Why this is safe
 *
 * It only ever *releases*, and only when the DOM says nothing is on screen to
 * justify a lock — no Element overlay, no drawer of ours. If a modal really is
 * open, every selector below matches and nothing is touched. The worst case is
 * doing nothing; there is no case where it hides something.
 *
 * Deliberately not a fix inside each dialog: the ones that legitimately close
 * themselves already release correctly, and this never runs while they are up.
 */
export default defineNuxtPlugin((nuxtApp) => {
  const router = useRouter();

  // Everything that has a right to hold the page still. `.el-overlay` covers
  // el-dialog, el-drawer and el-message-box; `.edit-modal-panel` is our own
  // right-hand drawer, which manages its own lock through
  // `composable/useBodyScrollLock`.
  const BLOCKERS = ".el-overlay, .el-drawer, .edit-modal-panel";

  const releaseIfNothingIsOpen = () => {
    if (typeof document === "undefined") return;
    if (document.querySelector(BLOCKERS)) return;

    const body = document.body;
    const locked =
      body.classList.contains("el-popup-parent--hidden") ||
      body.style.overflow === "hidden";

    if (!locked) return;

    body.classList.remove("el-popup-parent--hidden");
    body.style.removeProperty("overflow");
    // Element Plus also compensates for the scrollbar it removed; left behind,
    // it shows as a strip of dead space down the right-hand edge.
    body.style.removeProperty("padding-right");
  };

  /*
   * After the route settles, not during it. The outgoing page's teleported
   * modal nodes are removed as part of the transition, so checking any earlier
   * finds a `.el-overlay` that is on its way out and concludes, wrongly, that
   * something is still open. Two frames is enough for the removal to land
   * without being long enough to see.
   */
  router.afterEach(() => {
    nuxtApp.runWithContext(() =>
      requestAnimationFrame(() => requestAnimationFrame(releaseIfNothingIsOpen)),
    );
  });
});
